import { ReactNode, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

interface ProtectedClientRouteProps {
  children: ReactNode
}

const ProtectedClientRoute = ({ children }: ProtectedClientRouteProps) => {
  const { user, isLoading } = useAuth()
  const [isClient, setIsClient] = useState(false)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    const checkClientStatus = async () => {
      if (!user || !supabase) {
        setIsClient(false)
        setIsChecking(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('clients')
          .select('id')
          .eq('user_id', user.id)
          .single()

        if (error) throw error
        setIsClient(!!data)
      } catch (err) {
        console.error('Error checking client status:', err)
        setIsClient(false)
      } finally {
        setIsChecking(false)
      }
    }

    checkClientStatus()
  }, [user])

  if (isLoading || isChecking) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        {/* Floating orbs for glassmorphism background effect */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="floating-orb floating-orb-1"></div>
          <div className="floating-orb floating-orb-2"></div>
          <div className="floating-orb floating-orb-3"></div>
          <div className="floating-orb floating-orb-4"></div>
          <div className="floating-orb floating-orb-5"></div>
        </div>
        
        <div className="relative z-10 text-center">
          <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/client/auth" replace />
  }

  if (!isClient) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

export default ProtectedClientRoute 